import { Location, SubLocation, CreateLocationPayload, UpdateLocationPayload, CreateSubLocationPayload, UpdateSubLocationPayload } from '../types/location';
import { ApiErrorResponse } from '../types/auth';
import { PaginationParams, PaginatedResponse, buildQueryString } from '../types/pagination';
import { LOCATION_ENDPOINTS, SUBLOCATION_ENDPOINTS } from '../utils/endPoints';
import { createApiClient } from '../utils/axiosConfig';
import axios from 'axios';

const apiClient = createApiClient();

export const getAllLocations = async (params?: PaginationParams): Promise<PaginatedResponse<Location>> => {
  try {
    const qs = buildQueryString(params ?? {});
    const response = await apiClient.get<{ locations: Location[]; total: number; page: number; limit: number; totalPages: number }>(
      `${LOCATION_ENDPOINTS.ALL_LOCATIONS}${qs}`
    );
    const d = response.data;
    return {
      items: d.locations ?? [],
      total: d.total ?? 0,
      page: d.page ?? 1,
      limit: d.limit ?? 20,
      totalPages: d.totalPages ?? 0,
    };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to fetch locations');
    } else {
      console.error('Get all locations service error:', error);
      throw new Error('An unexpected error occurred while fetching locations.');
    }
  }
};

export const getSubLocationsByLocation = async (locationId: number): Promise<SubLocation[]> => {
  try {
    const response = await apiClient.get<{ subLocations: SubLocation[], message: string }>(LOCATION_ENDPOINTS.SUBLOCATIONS(locationId));
    return response.data.subLocations;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to fetch sublocations');
    } else {
      console.error('Get sublocations service error:', error);
      throw new Error('An unexpected error occurred while fetching sublocations.');
    }
  }
};

export const createLocation = async (payload: CreateLocationPayload): Promise<{ location: Location; message: string }> => {
  try {
    const response = await apiClient.post<{ location: Location; message: string }>(LOCATION_ENDPOINTS.CREATE, payload);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to create location');
    } else {
      console.error('Create location service error:', error);
      throw new Error('An unexpected error occurred while creating location.');
    }
  }
};

export const updateLocation = async (payload: UpdateLocationPayload): Promise<{ location: Location; message: string }> => {
  try {
    const response = await apiClient.put<{ location: Location; message: string }>(LOCATION_ENDPOINTS.UPDATE, payload);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to update location');
    } else {
      console.error('Update location service error:', error);
      throw new Error('An unexpected error occurred while updating location.');
    }
  }
};

export const createSubLocation = async (payload: CreateSubLocationPayload): Promise<{ sublocation: SubLocation; message: string }> => {
  try {
    const response = await apiClient.post<{ sublocation: SubLocation; message: string }>(SUBLOCATION_ENDPOINTS.CREATE, payload);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to create sublocation');
    } else {
      console.error('Create sublocation service error:', error);
      throw new Error('An unexpected error occurred while creating sublocation.');
    }
  }
};

export const updateSubLocation = async (payload: UpdateSubLocationPayload): Promise<{ sublocation: SubLocation; message: string }> => {
  try {
    const response = await apiClient.put<{ sublocation: SubLocation; message: string }>(SUBLOCATION_ENDPOINTS.UPDATE, payload);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to update sublocation');
    } else {
      console.error('Update sublocation service error:', error);
      throw new Error('An unexpected error occurred while updating sublocation.');
    }
  }
};

export const deleteLocation = async (locationId: number): Promise<{ message: string }> => {
  try {
    const response = await apiClient.delete<{ message: string }>(LOCATION_ENDPOINTS.DELETE(locationId));
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to delete location');
    } else {
      console.error('Delete location service error:', error);
      throw new Error('An unexpected error occurred while deleting location.');
    }
  }
};

export const deleteSubLocation = async (subLocationId: number): Promise<{ message: string }> => {
  try {
    const response = await apiClient.delete<{ message: string }>(SUBLOCATION_ENDPOINTS.DELETE(subLocationId));
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const apiError = error.response.data as ApiErrorResponse;
      throw new Error(apiError.message || 'Failed to delete sublocation');
    } else {
      console.error('Delete sublocation service error:', error);
      throw new Error('An unexpected error occurred while deleting sublocation.');
    }
  }
}; 